import { TId } from '@apia/util';
import { identifiers, parseTOKS } from './common';
import { ConstantsObject } from './ConstantsObject';

export const constantsPT: ConstantsObject = Object.freeze({
  ...identifiers,
  deliverables: [
    {
      title: 'Carta do Projeto',
      description:
        'Um documento que descreve o propósito, escopo, objetivos, partes interessadas e recursos para o projeto BPM, servindo como uma autorização formal.',
    },
    {
      title: 'Documentação do Processo Atual',
      description:
        'Documentação detalhada do estado atual dos processos de negócios, incluindo fluxogramas, descrições e métricas, fornecendo uma base para melhoria.',
    },
    {
      title: 'Relatório de Análise de Lacunas',
      description:
        'Uma análise que identifica as lacunas entre o estado atual e o estado futuro desejado dos processos de negócios, destacando ineficiências e áreas para melhoria.',
    },
    {
      title: 'Desenho do Processo Futuro',
      description:
        'Um desenho detalhado do estado futuro dos processos de negócios, incluindo fluxogramas de processos redesenhados e métricas de desempenho esperadas, visando abordar as lacunas identificadas.',
    },
    {
      title: 'Caso de Negócio',
      description:
        'Um documento que justifica o investimento no projeto BPM, detalhando os benefícios esperados, custos e retorno sobre investimento.',
    },
    {
      title: 'Plano de Implementação',
      description:
        'Um plano abrangente para executar o projeto BPM, incluindo cronogramas, recursos, papéis e responsabilidades, e marcos.',
    },
    {
      title: 'Plano de Gestão de Mudanças',
      description:
        'Uma estratégia para gerenciar a mudança organizacional associada ao projeto BPM, incluindo planos de comunicação e programas de treinamento.',
    },
    {
      title: 'Documento de Requisitos Tecnológicos',
      description:
        'Um documento que define os requisitos técnicos para suportar os processos redesenhados, incluindo software, hardware e integrações.',
    },
    {
      title: 'Plano de Teste e Casos de Teste',
      description:
        'Um plano para testar os processos redesenhados, incluindo cenários de teste, resultados esperados e critérios de sucesso.',
    },
    {
      title: 'Materiais de Treinamento',
      description:
        'Materiais desenvolvidos para treinar o pessoal nos novos processos, incluindo manuais, guias e módulos de aprendizagem online.',
    },
    {
      title: 'Relatórios de Status do Projeto',
      description:
        'Atualizações regulares fornecidas às partes interessadas sobre o progresso do projeto BPM, incluindo conquistas, problemas e próximos passos.',
    },
    {
      title: 'Relatório de Revisão Pós-Implementação',
      description:
        'Um documento que avalia o sucesso do projeto BPM após a implementação, incluindo os benefícios alcançados, métricas de desempenho e lições aprendidas.',
    },
  ],
  errors: {
    cannotImportConfigurationFile:
      'Não é possível processar o arquivo estabelecido como configuração do projeto. É um arquivo de configuração válido?',
    cannotDownloadZipFile:
      'Houve um erro ao baixar o arquivo de exportação do projeto.',
    cannotImportObjectsFromApia: 'Houve um erro ao importar elementos de Apia',
    cannotOpenCollectionSelector:
      'Houve um erro ao abrir o seletor de coleções',
    cannotProcessZipFile: `Houve um erro ao processar a documentação do zip. Foi baixada a versão padrão.`,
    cannotRemoveAttributeDueToForeignKey: (...entitiesList: string[]) =>
      parseTOKS(
        `Não é possível excluir a entidade atual porque ela está sendo usada nas chaves estrangeiras das seguintes entidades: <ul><TOK1></ul>`,
        entitiesList.map((c) => `<li>${c}</li>`).join(''),
      ),
    cannotRemoveInstanceDueToDeps: (entityTitle: string, entityName: string) =>
      parseTOKS(
        `Não é possível excluir a instância porque ela está sendo usada nas seguintes instâncias de entidades: <TOK1> (<TOK2>)`,
        entityTitle,
        entityName,
      ),
    cannotUpdateAttributeDueToForeignKey: (entitiesList: string[]) =>
      parseTOKS(
        `Não é possível atualizar a entidade atual porque ela está sendo usada nas chaves estrangeiras das seguintes entidades: <ul><TOK1></ul>`,
        entitiesList.map((c) => `<li>${c}</li>`).join(''),
      ),
    elementAlreadyExists: () => 'O nome incluído já está em uso',
  },
  explanations: {
    docPricingLegend:
      'Este documento oferece uma análise detalhada e fácil de entender dos custos do projeto, especificando minuciosamente os objetos que estão sendo desenhados para seu posterior desenvolvimento, as diferentes funções da equipe, as principais atividades e os prazos para garantir um planejamento financeiro transparente e eficaz.',
    excelActivitiesLegend:
      'A seguir, é apresentada a distribuição de atividades e funções. \n\nEsta informação, utilizada em conjunto com o cronograma de atividades, ajuda a determinar a atribuição de assistentes ao longo de todo o projeto.',
    excelItemsLegend: [
      'A seguir, é apresentada a lista completa dos itens considerados de interesse para o cliente.',
      'O documento enviado com a especificação de processos e objetos visa explicar a situação, além de boas práticas e conceitos gerais.',
      'Em particular, os indicadores, os documentos (gestão documental) e diversos casos de uso não estão incluídos no escopo e esforço da proposta.',
    ],
    excelPricingLegend:
      'Esta folha de Excel possui uma análise detalhada dos custos do projeto. Especifica-se os objetos de desenvolvimento, os diversas funções da equipe, as principais atividades e os prazos para garantir um planejamento financeiro transparente e eficaz.',
    excelScheduleLegend:
      'A seguir, é apresentada a cronologia para cada um dos itens de interesse. Cada coluna representa um período de 5 dias úteis.',
    elementsWithSimilarities:
      'Estes botões representam elementos que possuem objetos semelhantes dentro da estrutura do projeto. Ao clicar em qualquer um deles, será lançado um diálogo de fusão, facilitando a combinação de seus conteúdos.',
    generateForm:
      'Insira o conteúdo do formulário: nome do formulário, título do formulário, campos, tipos de campos, etc.',
    generateEntity:
      'Insira o conteúdo da entidade: nome da entidade, título da entidade, campos, tipos de campos, etc.',
    generateProcess:
      'Insira o conteúdo do processo: nome do processo, título do processo, títulos de tarefas, nomes de tarefas, títulos de formulários, nomes de formulários, títulos de campos, nomes, tipos, etc.',
    naming:
      'Esta funcionalidade permite determinar como nomes serão atribuídos a diferentes elementos da árvore. Prefixos serão atribuídos a todos os elementos. Sufixos só serão adicionados aos campos de formulário, onde o tipo de campo e o tipo de atributo serão avaliados para gerar uma composição deles: {PREFIX}_{FIELDNAME}_{ATTTYPE}_{FIELDTYPE}_[{GRID}?]',
    panelAutogenerated:
      'Este painel foi autogerado devido à criação do seu widget. Se desejar, consulte a definição do widget.',
    searchSimilaritiesModal:
      'Por favor, escolha uma coleção para buscar similaridades e especifique o limite de similaridade desejado. O limite representa a distância máxima permitida entre dois elementos para que sejam considerados semelhantes.',
    templates:
      'Estes modelos servem para várias funcionalidades ao definir a estrutura da documentação. Eles estão configurados como markdown interpolado, que suporta qualquer expressão de markdown válida. É possível  reorganizar as variáveis, marcadas por {VARIABLE_NAME}, ao longo do documento para personalizar o desenho conforme suas preferências.',
  },
  labels: {
    activity: () => 'Atividade',
    activities: () => 'Atividades',
    activitiesDefaults: () => 'Restaurar valores padrão',
    activitiesDistribution: () => 'Distribuição de atividades',
    activitiesFix: () => 'Corrigir atividades',
    activitiesProjectManagement: () => 'Gestão de Projetos',
    activitiesSummary: () => 'Resumo das atividades',
    activitiesTotal: () => 'Total (%)',
    activitiesTotalExplain: () =>
      'Cada atividade é atribuída entre as funções existentes e as porcentagens atribuídas para cada função devem somar 100%. Se não for assim, a coluna muda de cor para vermelha, indicando que a soma está errada.',
    activitiesSurveyAndScope: () => 'Pesquisa e Escopo',
    activitiesPrototype: () => 'Protótipo',
    activitiesPrototypeValidation: () => 'Validação de protótipo',
    activitiesFunctionalImplementation: () => 'Implementação funcional',
    activitiesTechnicalImplementation: () => 'Implementação técnica',
    activitiesTesting: () => 'Testes',
    activitiesFinalAdjustments: () => 'Ajustes finais',
    activitiesDocumentation: () => 'Documentação',
    activitiesTraining: () => 'Formação',
    activitiesInstallation: () => 'Instalação',
    activitiesDeployment: () => 'Desdobramento',
    add: () => 'Adicionar',
    addItem: (itemType: string) => parseTOKS('Adicionar <TOK1>', itemType),
    all: () => 'Todos',
    alteration: () => 'Alteração',
    applyToCurrentProject: () => 'Adicionar ao projeto',
    attention: () => 'Atenção',
    attribute: () => 'Atributo',
    attributes: () => 'Atributos',
    augmentForm: () => 'Ampliar formulário',
    augmentProcess: () => 'Ampliar processo',
    augmentPrompt: () => 'Mejorar prompt',
    binary: () => 'Visualização',
    bold: () => 'Negrito',
    bookSheets: () => 'Folhas de livro',
    both: () => 'Ambos',
    businessClass: () => 'Classe de negócio',
    businessClasses: () => 'Classes de negócio',
    button: () => 'Botão',
    byAttType: () => 'Por tipo de atributo',
    byFieldType: () => 'Por tipo de campo',
    cancel: () => 'Cancelar',
    cancellation: () => 'Cancelamento',
    cannotAggregate: () => 'Não é possível adicionar este elemento',
    category: () => 'Categoria',
    caution: () => 'Cuidado',
    chat: () => 'Chat',
    checkbox: () => 'Caixa de seleção',
    chooseExistingElement: () => 'Escolher elemento existente',
    codeBlock: () => 'Bloco de código',
    collection: () => 'Coleção',
    combo: () => 'Combobox',
    combobox: () => 'Combobox',
    companyIcon: () => 'Ícone da empresa',
    companyName: () => 'Nome da empresa',
    complexity: () => 'Complexidade',
    complexityLow: () => 'Baixa',
    complexityMid: () => 'Média',
    complexityHigh: () => 'Alta',
    complexityHours: () => 'Horas',
    complexityCustom: () => 'Personalizado',
    complexityFactors: () => 'Fatores de complexidade',
    complexityFactorsAreas: () => 'Áreas envolvidas',
    complexityFactorsAreasTooltip: () =>
      'O número de áreas funcionais, escritórios, grupos, etc. que utilizam o sistema pode potencialmente afetar a complexidade geral do projeto em até 20%.',
    complexityFactorsCounterpart: () => 'Contraparte treinada',
    complexityFactorsCounterpartTooltip: () =>
      'A contraparte pode realizar uma parte significativa do trabalho ou fornecer pouco apoio. Eles podem ter muita experiência em processos, mudança organizacional ou reengenharia ou não ter experiência alguma.',
    complexityFactorsIntegration: () => 'Integração',
    complexityFactorsIntegrationTooltip: () =>
      'A integração entre processos geralmente introduz complexidade adicional ao projeto.',
    complexityFactorsKpis: () => 'Kpis',
    complexityFactorsKpisTooltip: () =>
      'Os indicadores aumentam a complexidade do sistema. Um projeto com alta complexidade em indicadores pode aumentar a complexidade geral em até 20%.',
    complexityFactorsOrganizationalMaturity: () => 'Maturidade organizacional',
    complexityFactorsOrganizationalMaturityTooltip: () =>
      'Um fator que pode afetar a complexidade do sistema é o nível de conflito dentro da organização em relação ao projeto e sua capacidade de ser flexível e adaptar-se às mudanças.',
    complexityFactorsRate: () => 'Taxa',
    complexityFactorsTeamExperience: () => 'Experiência da equipe',
    complexityFactorsTeamExperienceTooltip: () =>
      'Um fator que pode afetar a produtividade do desenvolvimento do sistema é o nível de experiência da equipe de consultoria que trabalha no projeto. É recomendável contar com profissionais experientes em implementações de APIA em funções como gestão de projetos, consultoria funcional e desenho e programação de classes de negócio. A experiência extensa refere-se a mais de 2 anos, enquanto a experiência razoável indica mais de 9 meses.',
    complexityFactorsTecFunRelation: () => 'Relação Tec/Fun',
    complexityFactorsTecFunRelationTooltip: () =>
      'O cenário ideal é manter a relação técnica/funcional próxima a 30% para evitar uma complexidade técnica significativa, embora isso nem sempre seja gerenciável e dependa da natureza do projeto. Nunca deve exceder 50% em BPMS e raramente está abaixo de 30%.',
    complexityFactorsUsers: () => 'Quantidade de usuários',
    complexityFactorsUsersTooltip: () =>
      'A quantidade de usuários que utilizam o sistema pode potencialmente afetar a complexidade geral do projeto em até 20%',
    configuration: () => 'Configuração',
    convertToBusinessClass: () => 'Converter para classe de negócio',
    clear: () => 'Limpar',
    closed: () => 'Fechado',
    condition: () => 'Condição',
    content: () => 'Conteúdo',
    copy: () => 'Copiar',
    copyItem: (name: string = '', type: string = '') =>
      parseTOKS('Copiar <TOK1> <TOK2>', type, name),
    copyProject: () => 'Copiar projeto',
    cost: () => 'Custo',
    costInDays: () => 'Em dias',
    costInMonths: () => 'Em meses',
    costByHour: () => 'Por hora',
    costByProfile: () => 'Por perfil',
    costsHtml: () => 'Custos em HTML',
    count: () => 'Contar',
    createFunctionality: () => 'Criar funcionalidade',
    creation: () => 'Criação',
    cube: () => 'Cubo',
    custom: () => 'Personalizado',
    dashboard: () => 'Painel',
    dashboardPanel: () => 'Painel',
    dashboardPanels: () => 'Painéis',
    dashboards: () => 'Painéis',
    dataOriginEntities: () => 'Entidades de origem de dados',
    dataType: () => 'Tipo de dado',
    date: () => 'Data',
    definition: () => 'Definição',
    delete: () => 'Excluir',
    dependencies: () => 'Dependências',
    description: () => 'Descrição',
    details: () => 'Detalhes',
    deliverables: () => 'Entregáveis',
    deliverablesAlreadyExists: () => 'Já existe um entregável com esse nome',
    deliverablesEdit: () => 'Editar entregável',
    deliverablesNew: () => 'Novo entregável',
    deselectAll: () => 'Desmarcar tudo',
    didNotAugmentPrompt: () =>
      'Você está prestes a iniciar uma geração restrita, mas o prompt ainda não foi aprimorado. Isso pode não gerar o resultado esperado. Deseja prosseguir?',
    document: () => 'Documento',
    documents: () => 'Documentos',
    documentsForProcess: (proName: string) =>
      `Documentos do processo <div>${proName}</div>`,
    downloadFormat: () => 'Formato de download',
    dshDefault: () => 'Painel padrão',
    dshMobileDefault: () => 'Painel móvel padrão',
    editable: () => 'Editável',
    editItemsTemplate: () => 'Editar modelo de itens',
    editSpecsTemplate: () => 'Editar modelo de especificações',
    effortByItem: () => 'Esforço estimado por tipo de item',
    enabled: () => 'Habilitado',
    enableLocalCardinality: () => 'Habilitar cardinalidade local',
    entCreModalTitle: (entityName: string, entityTitle: string) =>
      parseTOKS(
        `Configuração de importação de entidade <TOK1> (<TOK2>)`,
        entityTitle,
        entityName,
      ),
    entCreTitle: (entityName: string, entityTitle: string) =>
      parseTOKS(
        'Configuração de importação para <TOK1> (<TOK2>)',
        entityTitle,
        entityName,
      ),
    entCreAdmType: () => 'Tipo de administração',
    entCreAltPro: () => 'Processo de alteração',
    entCreChkCreation: () => 'Criar entidade',
    entCreComboAtt: () => 'Atributo para mostrar em combo',
    entCreCreModal: () => 'Criar modal',
    entCreEntName: () => 'Nome da entidade',
    entCreEntPrefix: () => 'Prefixo da entidade',
    entCreCrePro: () => 'Processo de criação',
    entCreFrm: () => 'Criar formulário',
    entCreIgnorePK: () => 'Ignorar chaves primárias',
    entCreIsCod: () => 'Entidade de carga de código',
    entCreModalRetAtt: () => 'Atributo devolvido por modal',
    entCrePoolForProcesses: () => 'Pool para processos',
    entCreQry: () => 'Criar consulta',
    entities: () => 'Entidades',
    entity: () => 'Entidade',
    entityInstance: () => 'Instância',
    entityInstances: () => 'Instâncias',
    entityInstancesCount: (n: number) => `${n} instâncias`,
    entityInstanceAttribute: () => 'Atributo de instância de entidade',
    event: () => 'Evento',
    events: () => 'Eventos',
    excel: () => 'Excel',
    executable: () => 'Executável',
    exportApiaObject: (itemName: string) =>
      parseTOKS(`Criar em Apia: "<TOK1>"`, itemName),
    extended: () => 'Modo estendido',
    externalInterface: () => 'Interface externa',
    externalInterfaces: () => 'Interfaces externas',
    field: () => 'Campo',
    fields: () => 'Campos',
    fieldVisualization: () => 'Vista',
    file: () => 'Arquivo',
    findSimilarities: () => 'Encontrar semelhanças',
    foreignAttribute: () => 'Atributo estrangeiro',
    foreignCardinality: () => 'Cardinalidade estrangeira',
    foreignEntity: () => 'Entidade estrangeira',
    foreignKey: () => 'Chave estrangeira',
    foreignKeys: () => 'Chaves estrangeiras',
    form: () => 'Formulário',
    formField: () => 'Campo de formulário',
    formFields: () => 'Campos de formulário',
    formFromImage: () => 'Da imagem',
    formGenerateSuccess: () => 'Formulário gerado com sucesso',
    fromImage: () => 'Desde imagen',
    forms: () => 'Formulários',
    showContextForms: () => 'Ver formulários de contexto',
    free: () => 'Livre',
    functionality: () => 'Funcionalidade',
    generate: () => 'Gerar',
    generateAdditionalData: () => 'Gerar dados adicionais',
    generateAdditionalDataPreloaded: () =>
      'Gerar dados adicionais (pré-carregado)',
    generateBoth: () => 'Gerar ambas',
    generateEntity: () => 'Gerar entidade',
    generateForm: () => 'Gerar formulário',
    generateForms: () => 'Gerar formulários',
    generateImage: () => 'Gerar imagem',
    generateInstances: () => 'Gerar instâncias',
    generateObject: () => 'Gerar...',
    generateTasks: () => 'Gerar tarefas',
    generateProcess: () => 'Gerar processo',
    generateProcessFrom: (what: string) =>
      parseTOKS(`Gerar processo a partir de <TOK1>`, what),
    generateWithStrictAttributes: () => 'Estrito nos atributos',
    generateWithStrictFields: () => 'Estrito nos campos',
    generateWithStrictTasks: () => 'Rigoroso nas tarefas',
    getTabular: () => 'Obter vista estruturada',
    goBackToTask: (taskName: string) => `Voltar para ${taskName}`,
    goForwardToTask: (taskName: string) => `Ir para ${taskName}`,
    gotoEndTask: () => 'Pode ir ao final',
    gotoWhichTasks: () => 'Para quais tarefas vai',
    gotoFlowConfiguration: () => 'Configuração do fluxo',
    gotoTasksOrder: () => 'Ordem das tarefas',
    goal: () => 'Objetivo',
    grid: () => 'Tabela',
    height: () => 'Altura',
    hidden: () => 'Oculto',
    hideFunctionality: () => 'Ocultar funcionalidade',
    hours: () => 'Horas',
    html: () => 'Html',
    htmlCode: () => 'Código HTML',
    http: () => 'Http',
    iframe: () => 'Iframe',
    image: () => 'Imagem',
    imageDescription: () => 'Descrição da imagem',
    images: () => 'Imagens',
    importApiaObject: () => 'Importar de Apia',
    input: () => 'Entrada',
    instance: () => 'Instância',
    instances: () => 'Instâncias',
    internetSearch: () => 'Pesquisa na web',
    introduceFormDescription: () => 'Incluir descrição do formulário',
    invisible: () => 'Invisível',
    isStartTask: () => 'Tarefa inicial',
    items: () => 'Itens',
    itemsSummary: () => 'Resumo dos itens',
    kpi: () => 'KPI',
    kpiMaxValue: () => 'Valor máximo de KPI',
    kpiMinValue: () => 'Valor mínimo de KPI',
    kpiMultiplier: () => 'Multiplicador de KPI',
    kpiMultiThousanth: () => 'Milésimo',
    kpiMultiHundredth: () => 'Centésimo',
    kpiMultiTenth: () => 'Décimo',
    kpiMultiOne: () => 'Um',
    kpiMultiTen: () => 'Dez',
    kpiMultiHundred: () => 'Cem',
    kpiMultiThousand: () => 'Mil',
    kpiMultiTenthousand: () => 'Dez mil',
    kpiType: () => 'Tipo de KPI',
    kpiPeriodicityDaily: () => 'Diário',
    kpiPeriodicityMonthly: () => 'Mensal',
    kpiPeriodicityWeekly: () => 'Semanal',
    kpiPeriodicityYearly: () => 'Anual',
    kpiTypeCounter: () => 'Contador',
    kpiTypeOxford: () => 'Oxford',
    kpiTypeRing: () => 'Anel',
    kpiTypeScale: () => 'Escala',
    kpiTypeSpeedometer: () => 'Velocímetro',
    kpiTypeThermometer: () => 'Termômetro',
    kpiTypeTrafficLight: () => 'Semáforo',
    kpis: () => 'KPIs',
    java: () => 'Java',
    javascript: () => 'Javascript',
    jsp: () => 'JSP',
    keepExistingDefinition: () => 'Manter definição existente',
    label: () => 'Etiqueta',
    landingPage: () => 'Página inicial',
    landingPageDefault: () => 'Página inicial padrão',
    length: () => 'Comprimento',
    localAttribute: () => 'Atributo local',
    localCardinality: () => 'Cardinalidade local',
    mask: () => 'Máscara',
    mergeItems: (itemType: string) => parseTOKS(`Fusionar <TOK1>`, itemType),
    metadata: () => 'Metadados',
    model: () => 'Modelo',
    name: () => 'Nome',
    naming: () => 'Nomeação',
    newEntityTitle: (entityTitle: string) =>
      parseTOKS(`Nova instância (<TOK1>)`, entityTitle),
    noImage: () => 'Sem imagem',
    noItemsToAutofill: () => 'Não há itens para preenchimento automático',
    noDocsSelected: () => 'Pelo menos um documento deve ser selecionado',
    none: () => 'Nenhum',
    nonStrictGenerationWarning: () =>
      'Você está prestes a iniciar uma geração não restrita. Isso pode não ser o que você deseja. Tem certeza de que deseja continuar?',
    nothingToMerge: () => 'Não há itens semelhantes na coleção selecionada',
    noSheetsSelected: () => 'Pelo menos uma folha deve ser selecionada',
    notIncluded: () => 'Não incluído',
    noTitleOnAddedElement: (type: string, name: string) =>
      parseTOKS(
        `O elemento "<TOK1>" do tipo "<TOK2>" deve ter um título`,
        name,
        type,
      ),
    number: () => 'Número',
    objects: () => 'Objetos',
    objectsCount: (count: number | string) =>
      parseTOKS(`<TOK1> elementos`, count),
    objectsNames: () => 'Nomes de objetos (separados por vírgulas)',
    objectType: () => 'Tipo de objeto',
    objectDependencies: () => 'Ocorrências do item:',
    omit: () => 'Omitir',
    omitEverything: () => 'Omitir tudo',
    omittedItems: () => 'Itens omitidos',
    openCube: () => 'Abrir cubo',
    openQuery: () => 'Abrir consulta',
    openWidgetDefinition: () => 'Abrir definição de widget',
    options: () => 'Opções',
    other: () => 'Outro',
    outcome: () => 'Resultado',
    outcomes: () => 'Resultados',
    palette: () => 'Paleta',
    paletteBackgroundDefault: () => 'Fundo padrão',
    paletteBackgroundPaper: () => 'Papel de fundo',
    palettePrimaryMain: () => 'Primário',
    palettePrimaryContrastText: () => 'Texto de contraste primário',
    paletteSecondaryMain: () => 'Secundário',
    paletteSecondaryContrastText: () => 'Texto de contraste secundário',
    paletteTextPrimary: () => 'Texto normal',
    paletteTextTitle: () => 'Títulos',
    panel: () => 'Painel',
    panels: () => 'Painéis',
    parameters: () => 'Parâmetros',
    paste: () => 'Colar',
    path: () => 'Rota',
    performer: () => 'Intérprete',
    performers: () => 'Intérpretes',
    periodicity: () => 'Periodicidade',
    pool: () => 'Pool',
    pools: () => 'Pools',
    position: () => 'Posição',
    prefix: () => 'Prefixo',
    prefixes: () => 'Prefixos',
    pricing: () => 'Preços',
    pricingIncludedObjects: () => 'Objetos incluídos',
    project: () => 'Projeto',
    projectDetails: (projectName: string) => parseTOKS('<TOK1>', projectName),
    prompt: () => 'Pronto',
    process: () => 'Processo',
    processes: () => 'Processos',
    processEntity: (proName: string) =>
      `Entidade principal do processo <div>${proName}</div>`,
    processTask: () => 'Tarefa do processo',
    processTasks: () => 'Tarefas do processo',
    profile: () => 'Perfil',
    profiles: () => 'Perfiles',
    profileTotalHours: () => 'Total de horas do perfil',
    projectDocumentation: (projectName: string) =>
      parseTOKS(`Documentação de <TOK1>`, projectName),
    projectEstimate: (projectName: string) =>
      parseTOKS(`Estimativa para o projeto '<TOK1>'`, projectName),
    projectIcon: () => 'Ícone do projeto',
    projectName: () => 'Nome do projeto',
    projectPlan: () => 'Plano de projeto',
    properties: () => 'Propriedades',
    proposal: () => 'Proposta',
    proposalCost: () => 'Custo da proposta',
    proposalEffort: () => 'Esforço da proposta',
    proposalScope: () => 'Escopo da proposta',
    projectTypes: () => 'Tipos de projeto',
    query: () => 'Consulta',
    queries: () => 'Consultas',
    radio: () => 'Rádio',
    readonly: () => 'Somente leitura',
    react: () => 'React',
    reference: () => 'Referência',
    references: () => 'Referências',
    refresh: () => 'Atualizar',
    refreshOnFocus: () => 'Atualizar ao receber o foco',
    regenerateEntity: () => 'Regerar entidade',
    regenerateForm: () => 'Regerar formulário',
    regenerateProcess: () => 'Regerar processo',
    regexp: () => 'Expressão regular',
    relatedProcess: () => 'Processo relacionado',
    relatedProcesses: () => 'Processos relacionados',
    relatedProcessesForProcess: (proName: string) =>
      `Processos relacionados a <div>${proName}</div>`,
    removable: () => 'Removível',
    remove: () => 'Eliminar',
    removeElementTitle: (elementType: string, title: string) =>
      parseTOKS(`[<TOK1>] <TOK2>`, elementType, title),
    removeItem: (elementType: string, itemTitle: string) =>
      parseTOKS(`Eliminar <TOK1> <TOK2>`, elementType, itemTitle),
    removeSources: () => 'Eliminar fontes',
    replaceExistingPrompt: () => 'Substituir prompt atual',
    replaceWithNewElement: () => 'Substituir com nova definição',
    removeStateFromEntityWarning: () =>
      'Eliminar este estado da entidade também eliminará o estado de todos os processos que o contiverem.',
    renameExistingProject: () => 'Renomear elemento existente',
    renameConflictingElement: () => 'Renomear elemento em conflito',
    required: () => 'Requerido',
    rest: () => 'Rest',
    result: () => 'Resultado',
    role: () => 'Função',
    roles: () => 'Funções',
    rolesAndActivities: () => 'Funções e atividades',
    rolesBlackBoxTester: () => 'Teste de caixa preta',
    rolesExternalFunctionalConsultant: () => 'Consultor funcional externo',
    rolesFunctionalConsultant: () => 'Consultor funcional',
    rolesProjectManager: () => 'Gerente de projetos',
    rolesSalesExecutive: () => 'Executivo de vendas',
    rolesServicesDirector: () => 'Diretor de serviços',
    rolesTechnicalSolutionArchitect: () => 'Arquiteto de soluções técnicas',
    rolesTechnicalComponentsProgrammer: () =>
      'Programador de componentes técnicos',
    routine: () => 'Rutina',
    rule: () => 'Regra',
    rules: () => 'Regras de negócio',
    rulesForProcess: (proName: string) =>
      `Regras do negócio para o processo <div>${proName}</div>`,
    schedule: () => 'Schedule',
    scheduleExcelTitle: () =>
      'Horário de atividades, funções, entregáveis e processos',
    scheduleCollaboratorsAverage: () => 'Média de colaboradores necessários',
    scheduleCollaboratorsMax: () => 'Máximo de colaboradores necessários',
    scheduleCollaboratorsMin: () => 'Mínimo de colaboradores necessários',
    scheduleFromTo: (from: TId, to: TId) =>
      parseTOKS(`Dia <TOK1> até o dia <TOK2>`, from, to),
    scheduleExplain: () =>
      '<p>Especifique a quantidade de dias úteis que planeja dedicar a este projeto e detalhe a participação dos colaboradores ao longo do tempo. Isso determinará diretamente o número de colaboradores necessários para cumprir com o prazo desejado.</p><p>Normalmente, um projeto comum tem um prazo de sessenta dias úteis.</p>',
    scheduleExplainPricing: () =>
      'Insira o preço por hora de cada função para calcular o custo total.',
    scheduleHoursCount: (count: TId) => parseTOKS(`<TOK1>h`, count),
    schedulePeopleCount: (count: TId) => parseTOKS(`<TOK1> pessoas`, count),
    schedulePeopleCountMin: (count: TId) => parseTOKS(`<TOK1>p`, count),
    schedulePeopleMax: (count: TId, from: TId, to: TId) =>
      parseTOKS(`<TOK1> pessoas (Dia <TOK2> ao <TOK3>)`, count, from, to),
    schedulePeriod: () => 'Período',
    scheduleTitleCollaboratorsDetails: () =>
      'Detalhes semanais dos colaboradores',
    scheduleTitleCollaboratorsSummary: () => 'Resumo dos colaboradores',
    scheduleTitleDeliverablesSchedule: () => 'Prazo de entregáveis',
    scheduleTitleObjectsSchedule: () => 'Prazo para objetos',
    scheduleTitleDiagram: () => 'Schedule de distribuição de atividades',
    scheduleTitlePricing: () => 'Preços',
    scope: () => 'Escopo',
    search: () => 'Buscar',
    section: () => 'Seção',
    sections: () => 'Seções',
    selectAll: () => 'Selecionar tudo',
    selectWeek: () => 'Selecionar semana',
    showComments: () => 'Mostrar comentários',
    showHistory: () => 'Mostrar histórico',
    showInformation: () => 'Mostrar informação',
    showRefresh: () => 'Mostrar atualização',
    showDetailsForItem: (itemName: string) =>
      parseTOKS(`Mostrar detalhes de <TOK1>`, itemName),
    showInSiteMap: () => 'Mostrar no mapa do site',
    showValue: () => 'Mostrar valor',
    similaritiesFound: () => 'Semelhanças encontradas',
    size: () => 'Tamanho',
    soa: () => 'SOA',
    somethingWrongGenForms: () =>
      'Houve um erro na geração do formulário, por favor tente novamente.',
    sourceItems: () => 'Itens de origem',
    sortProcessTasks: (processTitle: string) =>
      parseTOKS(`Ordenar tarefas de '<TOK1>'`, processTitle),
    specsHTML: () => 'Especificações HTML',
    srcName: () => 'Nome de origem',
    state: () => 'Estado',
    states: () => 'Estados',
    statesForProcess: (proName: string) =>
      `Estados do processo <div>${proName}</div>`,
    status: () => 'Status',
    string: () => 'String',
    structured: () => 'Estruturado',
    structuredExamples: () => 'Exemplos estruturados',
    suffixes: () => 'Sufixos',
    summary: () => 'Resumo',
    summaryAttributesHours: () => 'Atributos',
    summaryBusinessClassesHours: () => 'Classes de negócio',
    summaryDocumentsHours: () => 'Documentos',
    summaryEntitiesHours: () => 'Entidades',
    summaryExternalInterfacesHours: () => 'Interfaces externas',
    summaryFormsHours: () => 'Formulários',
    summaryKpisHours: () => 'KPIs',
    summaryFixedCost: () => 'Custo fixo',
    summaryVariableCost: () => 'Custo variável',
    summaryProcessHours: () => 'Processo',
    summarySubtotal: () => 'Subtotal',
    summaryFactors: () => 'Fatores de complexidade',
    summaryTotal: () => 'Total',
    supervisor: () => 'Supervisor',
    tab: () => 'Exibir como aba',
    targetItem: () => 'Item alvo',
    task: () => 'Tarefa',
    tasks: () => 'Tarefas',
    tasksForProcess: (proName: string) =>
      `Tarefas do processo <div>${proName}</div>`,
    taskForProcess: (tskName: string, proName: string) =>
      `Tarefa ${tskName} <div>do processo ${proName}</div>`,
    template: () => 'Modelo',
    templates: () => 'Modelos',
    textarea: () => 'Área de texto',
    theme: () => 'Tema',
    threshold: () => 'Limite',
    title: () => 'Título',
    tooMuchFields: () =>
      'O prompt atual parece conter muitos campos. Isso pode resultar em custos de uso elevados. Tem certeza de que deseja continuar?',
    semiStructuredPromptRequired: () =>
      'Não é possível gerar o processo até que exista um prompt semiestruturado válido. Gere ou ajuste o prompt central a partir do chat antes de continuar.',
    totalHours: () => 'Horas totais',
    transcribeAudio: () => 'Transcrever áudio',
    trigger: () => 'Gatilho',
    triggers: () => 'Gatilhos',
    type: () => 'Tipo',
    unnamedProject: () => 'Projeto sem nome',
    uploadFromDisk: () => 'Carregar do disco',
    useCase: () => 'Caso de uso',
    useCases: () => 'Casos de uso',
    useCasesForProcess: (proName: string) =>
      `Casos de uso do processo <div>${proName}</div>`,
    user: () => 'Usuário',
    users: () => 'Usuários',
    value: () => 'Valor',
    view: () => 'Vista',
    visible: () => 'Visível',
    week: (no?: number) => parseTOKS(`Semana n° <TOK1>`, no ?? ''),
    weekNo: () => 'Número da semana',
    weekSummary: () => 'Resumo da semana',
    widget: () => 'Widget',
    widgetPanel: () => 'Painel de widgets',
    widgets: () => 'Widgets',
    whyShouldFix: () =>
      'Este botão ajusta os tempos incluidos para ter um total de 1 por cada função em cada atividade',
  },
  questions: {
    augmentProcessIntroduction: () =>
      `Por favor, insira abaixo a mensagem de personalização do aumento`,
    augmentProcessPrompt: (process: {
      name: string;
      title?: string;
      description?: string;
    }) =>
      parseTOKS(
        `<TOK1><TOK2>`,
        process.title ? `${process.title}\n\n` : '',
        process.description
          ? `Descrição do processo: ${process.description}`
          : '',
      ),
    cannotReadFile: () => 'Não é possível ler o arquivo como projeto',
    cannotRemoveBecauseOfDependencies: () =>
      `Eliminar este item também eliminará as seguintes rotas de dependência. Deseja continuar?:`,
    changesWillBeApplied: () =>
      'Os elementos do projeto serão criados no ambiente atual. Esta ação é irreversível. Deseja continuar?',
    changesWillBeLost: () => 'As alterações serão perdidas',
    chooseExistingElement: (elementType: string) =>
      parseTOKS(`Escolha <TOK1>`, elementType),
    cloneNewName: () => `Por favor, insira o nome do novo elemento`,
    couldNotGetProcess: () => 'Houve um erro ao obter o processo',
    createItem: (itemName: string) => parseTOKS(`Novo <TOK1>`, itemName),
    doTheSameForEvery: (itemType: string) =>
      parseTOKS(`Faça o mesmo para cada <TOK1>`, itemType),
    doThemSameForEverything: () => 'Faça o mesmo para tudo',
    doYouWishToContinue: () => 'Deseja continuar?',
    editForeignKeyConsequences: () =>
      'Editar uma chave estrangeira eliminará todas as suas instâncias. Deseja continuar?',
    itemAlreadyAdded: (itemName: string) =>
      parseTOKS(`Item com nome: <TOK1> já foi adicionado ao projeto`, itemName),
    makeNewForeignKeyConsequences: () =>
      'Adicionar uma nova chave estrangeira eliminará todas as suas instâncias. Deseja continuar?',
    removeInstancesAttributesAsWell: () =>
      'Eliminar este atributo da entidade também eliminará os valores dos atributos das instâncias. Deseja continuar?',
    removeOrphanElements: () => 'Esta ação eliminará estes elementos órfãos:',
    removeTheElement: () => `Esta ação eliminará o elemento`,
    removeWidgetConfirm: () =>
      'Tem certeza de que deseja eliminar este widget? Isso também eliminará os painéis gerados automaticamente.',
    renameAlreadyUsedName: () =>
      `O nome escolhido já está em uso, por favor escolha outro`,
    thereIsNothingToRemove: () => 'Não há nada para eliminar',
    updateFieldClone: () => 'Clonar e substituir',
    updateFieldConfirm: () => 'Sobrescrever',
    updateField: ({ TOK1 }: { TOK1: string }) =>
      parseTOKS(`Tem certeza de que deseja atualizar <TOK1>?`, TOK1),
    updateAffectedFields: () => 'Esta mudança afetará as seguintes rotas:',
  },
  tools: {
    addProject: 'Adicionar definição ao projeto atual',
    advanced: 'Avançado',
    apply: 'Aplicar',
    autofill: 'Autopreenchimento',
    basic: 'Básico',
    close: 'Fechar',
    commandPalette: 'Paleta de comandos (Ctrl + Q)',
    configuration: 'Configuração',
    collapse: 'Fechar todos os nós',
    distance: 'Buscar objetos semelhantes',
    document: 'Imprimir documentação',
    export: 'Exportar',
    import: 'Importar',
    intermediate: 'Intermediário',
    merge: 'Fusionar',
    new: 'Novo',
    notes: 'Notas (Ctrl+Shift+E)',
    open: 'Abrir',
    projectPlan: 'Plano de projeto',
    redo: 'Refazer',
    save: 'Guardar',
    saveConfiguration: 'Guardar configuração',
    saveDeliverables: 'Guardar entregáveis',
    saveNaming: 'Guardar nomenclatura',
    saveParameters: 'Guardar parâmetros',
    saveRolesAndActivities: 'Guardar funções e atividades',
    saveTemplates: 'Guardar modelos',
    sort: 'Ordenar',
    sortByNameAsc: 'Ordenar por nome ascendente',
    sortByNameDesc: 'Ordenar por nome descendente',
    sortByTitleAsc: 'Ordenar por título ascendente',
    sortByTitleDesc: 'Ordenar por título descendente',
    undo: 'Desfazer',
    vacuum: 'Vazio',
  },
});
